import { isNonEmpty } from './../../../utils/validators';
/**
 * This file defines how controls are structured into sections
 * and associated with each and every visualization type.
 */

export const commonSections = {
};

const getMetricSecondaryExclusiveOverrides = (secondaryDimensionName: string) => {
  return {
    metric_multiple: {
      additionalMapStateToProps: (state) => {
        /**
         * If secondary dimension is selected, then only allow single metric
         */
        const controls = state.controls;
        if (controls[secondaryDimensionName] && controls[secondaryDimensionName].value) {
          return { maxSelected: 1 };
        }
        return {};
      },
    },
    [secondaryDimensionName]: {
      additionalMapStateToProps: (state) => {
        /**
         * If multiple metrics are selected, then hide secondary dimension
         */
        const controls = state.controls;
        if (controls.metric_multiple &&
          controls.metric_multiple.value &&
          controls.metric_multiple.value.length > 1) {
          return { hidden: true };
        }
        return {};
      },
    },
  };
};

const primaryTimeGrainAdditionalMap = (state) => {
  /**
   * Hide primary time grain if primary dimension is not time
   */
  const controls = state.controls;
  if (controls.primary_dimension
    && controls.primary_dimension.value !== 'time'
    )  {
    return { hidden: true };
  }
  return {};
};

const secondaryTimeGrainAdditionalMap = (state) => {
  /**
   * Hide secondary time grain if secondary dimension is not time
   */
  const controls = state.controls;
  if (controls.secondary_dimension && controls.secondary_dimension.value !== 'time') {
    return { hidden: true };
  }
  return {};
};

// Control Override for line
const controlOverrideForLine: any =
  getMetricSecondaryExclusiveOverrides('secondary_dimension');
controlOverrideForLine.secondary_dimension.withTime = false;
controlOverrideForLine.primary_dimension_time_grain = { validators: [isNonEmpty] };

const controlOverrideForTrendLine: any = {
  primary_dimension_time_grain: {
    additionalMapStateToProps:  ({ controls }) => {
      if (!controls.show_trend_line.value) {
        return { hidden: true };
      }
      return {};
    },
  },
  aggregation: {
    additionalMapStateToProps: ({ controls }) => {
      if (!controls.show_trend_line.value) {
        return { hidden: true };
      }
      return {};
    },
  },
};

export const visTypes = {
  line: {
    label: 'Time Series - Line Chart',
    showOnExplore: true,
    controlPanelSections: [
      {
        label: 'Query',
        expanded: true,
        controlSetRows: [
          ['duration'],
          ['primary_dimension_time_grain'],
          ['metric_multiple'],
          ['secondary_dimension'],
          ['filter'],
        ],
        metricSecondaryExclusive: true,
      },
      {
        label: 'Chart Options',
        expanded: true,
        controlSetRows: [
          ['x_axis_label', 'y_axis_label'],
        ],
      },
    ],
    controlOverrides: controlOverrideForLine,
  },

  dual_time: {
    label: 'Dual Axis Line Chart',
    showOnExplore: true,
    controlPanelSections: [
      {
        label: 'Query',
        expanded: true,
        controlSetRows: [
          ['duration'],
          ['primary_dimension_time_grain'],
          ['metric'],
          ['metric_2'],
          ['filter'],
        ],
      },
      {
        label: 'Chart Options',
        expanded: true,
        controlSetRows: [
          ['x_axis_label'],
          ['y_axis_label', 'y_axis_2_label'],
        ],
      },
    ],
    controlOverrides: {
      primary_dimension_time_grain: {
        validators: [isNonEmpty],
      },
    },
  },
  bar: {
    label: 'Bar Chart',
    controlPanelSections: [
      {
        label: 'Query',
        expanded: true,
        controlSetRows: [
          ['duration'],
          ['bar_type'],
          ['metric_multiple'],
          ['primary_dimension'],
          ['primary_dimension_time_grain'],
          ['secondary_dimension'],
          ['secondary_dimension_time_grain'],
          ['filter'],
        ],
      },
      {
        label: 'Chart Options',
        expanded: true,
        controlSetRows: [
          ['bar_stacked'],
          ['x_axis_label', 'y_axis_label'],
        ],
      },
    ],
    controlOverrides: {
      ...getMetricSecondaryExclusiveOverrides('secondary_dimension'),
      primary_dimension_time_grain: {
        additionalMapStateToProps: primaryTimeGrainAdditionalMap,
      },
      secondary_dimension_time_grain: {
        additionalMapStateToProps: secondaryTimeGrainAdditionalMap,
      },
    },
  },

  dual_bar: {
    label: 'Dual Axis Bar Chart',
    controlPanelSections: [
      {
        label: 'Query',
        expanded: true,
        controlSetRows: [
          ['duration'],
          ['metric'],
          ['metric_2'],
          ['primary_dimension'],
          ['primary_dimension_time_grain'],
          ['secondary_dimension'],
          ['secondary_dimension_time_grain'],
          ['filter'],
        ],
      },
      {
        label: 'Chart Options',
        expanded: true,
        controlSetRows: [
          ['x_axis_label'],
          ['y_axis_label', 'y_axis_2_label'],
        ],
      },
    ],
    controlOverrides: {
      primary_dimension_time_grain: {
        additionalMapStateToProps: primaryTimeGrainAdditionalMap,
      },
      secondary_dimension_time_grain: {
        additionalMapStateToProps: secondaryTimeGrainAdditionalMap,
      },
    },
  },
  pie: {
    label: 'Pie Chart',
    controlPanelSections: [
      {
        label: 'Query',
        expanded: true,
        controlSetRows: [
          ['duration'],
          ['metric'],
          ['primary_dimension'],
          ['primary_dimension_time_grain'],
          ['filter'],
        ],
      },
      {
        label: 'Chart Options',
        expanded: true,
        controlSetRows: [
          ['doughnut'],
        ],
      },
    ],
    controlOverrides: {
      primary_dimension_time_grain: {
        additionalMapStateToProps: primaryTimeGrainAdditionalMap,
      },
    },
  },
  exception: {
    label: 'Exception Card',
    controlPanelSections: [{
      label: 'Query',
      expanded: true,
      controlSetRows: [
        ['exception_rule'],
        ['exception_card_type'],
      ],
    }, {
      label: 'Chart Options',
      expanded: true,
      controlSetRows: [],
    }],
  },
  number_with_trend_lines: {
    label: 'Big Number with Trendline',
    controlPanelSections: [{
      label: 'Query',
      expanded: true,
      controlSetRows: [
        ['show_trend_line'],
        ['aggregation'],
        ['primary_dimension_time_grain'],
        ['duration'],
        ['metric'],
        ['filter'],
      ],
    }, {
      label: 'Chart Options',
      expanded: true,
      controlSetRows: [],
    }],
    controlOverrides: controlOverrideForTrendLine,
  },
};
const linkToDashboardControl = new  Object({
  label: 'linkTo',
  controlSetRows: [
      ['linkTo'],
  ],
});

const colorPickerControl = new Object({
  label: 'colorPicker',
  controlSetRows: [
    ['colorPicker'],
  ],
});

export default visTypes;

export const sectionsToRender = (visType) => {
  const vis = visTypes[visType];
  const controlsToReturn = vis ?
  [...vis.controlPanelSections, colorPickerControl, linkToDashboardControl] :
    [linkToDashboardControl];
  return controlsToReturn
  .filter(section => section);
};
